// "Вземи изгодно": up to three products beside the cart that go with what the
// buyer is adding — bought together with it, else of its series, author,
// category or vendor (aldoo-com-api commerce/lib/upsell). Each is listed, in
// stock and not in the cart; "+ Добави" puts it there, and the next ask
// offers another in its place. A product that needs a choice first (a size, a
// color) opens a small product page over the cart (UpsellProductPanel).
//
// On while the store's config says so (STORE_CONFIG.upsell); asked again a
// moment after the cart's products change.
import React, { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { PlusIcon, TruckIcon, CursorArrowRaysIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Img from "../Img"
import PriceDisplay from "../PriceDisplay"
import { basePrice } from "../../utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../../utils/discountUtils"
import useT from "../../hooks/useT"
import useSiteLanguage from "../../hooks/useSiteLanguage"
import { useCurrency } from "../../store/currency"
import { ListProducts, LIST_FIELDS } from "../../api"
import { CartUpsell as AskUpsell } from "../../storeApi"
import { buildContentUrl } from "../../utils/contentRoutes"
import { addToCart } from "../../store/cart"
import { authorNames, brandName } from "../ProductBox"
import { needsSelection } from "aldoo-ra/Store/extensions"
import UpsellProductPanel from "./UpsellProductPanel"
import { listOf, trackListImpression, trackSelectItem } from "../../tracking"

const UPSELL_LIST = listOf("cart_upsell")

/** "Free delivery", as a chip: green, in capitals, a truck before it — it stands out in a line of text. */
export function FreeDeliveryChip({ label, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[12px] font-bold uppercase tracking-wide text-success whitespace-nowrap align-middle ${className}`} data-testid="free-delivery-chip">
      <TruckIcon className="w-3.5 h-3.5 shrink-0" />
      {label}
    </span>
  )
}

const DELAY = 300

/** The discount first, then the price paid, then the struck original: what the offer is, at a glance. */
function OfferPrice({ product }) {
  const { currency, convert } = useCurrency()
  const base = basePrice(product)
  if (!base) return null
  const amount = convert(base.amount, base.currency)
  const percent = activeDiscountPercent(product)
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid="cart-upsell-price">
      {percent > 0 && <span className="inline-flex shrink-0 h-[22px] items-center rounded bg-primary_hov px-2 text-sm font-condensed font-medium text-white">-{percent}%</span>}
      <PriceDisplay amount={applyDiscount(amount, percent)} currency={currency} variant="small" className={percent > 0 ? "min-w-0 text-primary" : "min-w-0"} />
      {percent > 0 && <PriceDisplay amount={amount} currency={currency} variant="small_struck" struck className="opacity-50 self-start pt-[2px]" />}
    </span>
  )
}

export default function CartUpsell({ store, max = 3 }) {
  const t = useT()
  const language = useSiteLanguage()
  const { currency, convert } = useCurrency()
  const [products, setProducts] = useState([])
  // the list as shown, for the next answer: what it was asked in (language, currency)
  const shown = useRef([])
  const sectionRef = useRef(null)
  // a product that needs a choice (a size, a color) opens over the cart to make it
  const [choosing, setChoosing] = useState(null)
  const add = (product) => (needsSelection(product) ? setChoosing(product) : addToCart(product, 1, {}, { placement: "cart_upsell" }))
  // the newest line leads: the cart keeps the order the lines were added in
  const pids = [...new Set((store.cart || []).map((line) => line.pid))].reverse()
  // the quantities too: a seller's basket reaching free delivery changes what is offered
  const lines = (store.cart || []).map((line) => ({ pid: line.pid, quantity: line.quantity || 1 }))
  const key = lines.map((line) => `${line.pid}:${line.quantity}`).join(",")

  useEffect(() => {
    if (!pids.length) return setProducts([])
    let alive = true
    const timer = setTimeout(async () => {
      try {
        // a few spare picks beyond what is shown: a slot freed by an add is filled from them
        const answer = await AskUpsell(pids, Math.min(9, max * 3), lines)
        const wanted = (answer?.list || []).map((entry) => entry.pid)
        if (!wanted.length && !shown.current.length) return alive && setProducts([])
        const listed = wanted.length ? await ListProducts({ page: 0, pids: wanted, items_per_page: wanted.length, language, currency, owned_only: false, fields: LIST_FIELDS }) : null
        const byPid = new Map((listed?.list || []).map((product) => [product.pid, product]))
        const ranked = wanted.map((pid) => byPid.get(pid)).filter(Boolean)
        if (!alive) return
        const inCart = new Set(lines.map((line) => line.pid))
        setProducts((now) => {
          // the first list, or one in another language / currency: the server's picks, the dearest on top
          if (!now.length || shown.current.basis !== `${language}:${currency}`) {
            const paid = (product) => {
              const base = basePrice(product)
              return base ? applyDiscount(convert(base.amount, base.currency), activeDiscountPercent(product)) : 0
            }
            return ranked.slice(0, max).sort((a, b) => paid(b) - paid(a))
          }
          // after that, what is shown stays where it is: only a product now in the cart (the one just
          // added) gives its place to the next pick not shown yet
          const kept = new Set(now.filter((product) => !inCart.has(product.pid)).map((product) => product.pid))
          const spare = ranked.filter((product) => !kept.has(product.pid) && !inCart.has(product.pid))
          const next = now.map((product) => (inCart.has(product.pid) ? spare.shift() || null : product)).filter(Boolean)
          // a list that was short gets what more there is
          while (next.length < max && spare.length) next.push(spare.shift())
          return next
        })
        shown.current.basis = `${language}:${currency}`
      } catch {
        if (alive) setProducts([])
      }
    }, DELAY)
    return () => {
      alive = false
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, language, currency, max])

  // the suggestions on screen (GA4 view_item_list): each once per page
  useEffect(() => {
    products.forEach((product, index) => trackListImpression(product, UPSELL_LIST, index))
  }, [products])

  if (!products.length) return null
  return (
    <>
      {/* the divider, its words in a label in the middle — as the discount labels look (OfferPrice's -10%):
          a click brings every suggestion into view (the cart's list scrolled to its end). A child of the
          cart's scrolling list itself, sticky at its foot: while the suggestions are below what is in
          view, it stays at the bottom to be clicked; scrolled to them, it sits in its place */}
      <div ref={sectionRef} className="sticky bottom-0 z-[1] -mx-5 px-5 mt-4 flex items-center gap-3 py-2 bg-paper" data-testid="cart-upsell-divider">
        <span className="h-px flex-1 bg-bordered" />
        <button
          type="button"
          onClick={() => {
            const list = sectionRef.current?.closest('[data-testid="cart-lines"]')
            if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" })
            else sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
          }}
          className="inline-flex items-center gap-1.5 h-[26px] rounded bg-primary_hov px-2.5 text-sm font-condensed font-medium uppercase tracking-wide text-white hover:brightness-110 transition"
          data-testid="cart-upsell-jump"
        >
          <CursorArrowRaysIcon className="w-4 h-4 stroke-2" />
          {t`Get a good deal`}
        </button>
        <span className="h-px flex-1 bg-bordered" />
      </div>
      <section className="pb-2" data-testid="cart-upsell">
      <div className="flex flex-col">
        <AnimatePresence initial={false} mode="popLayout">
        {products.map((product) => (
          <motion.div
            key={product.pid}
            layout
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -32 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="flex items-center gap-3 py-3 border-t border-bordered first:border-t-0"
            data-testid="cart-upsell-item"
            data-pid={product.pid}
          >
            <Button
              variant="none"
              href={buildContentUrl(product)}
              onClick={() => {
                trackSelectItem(product, UPSELL_LIST)
                store.hideCart()
              }}
              className="shrink-0"
            >
              <Img src={product.square || product.thumb} alt={product.name} width={56} height={56} className="w-[56px] h-[56px] rounded-md overflow-hidden" />
            </Button>
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <span className="text-[14px] font-semibold leading-tight line-clamp-2">{product.name}</span>
              {/* the author, as the cards name it (the brand for goods) */}
              {(authorNames(product) || brandName(product)) && <span className="text-[12px] text-text_secondary truncate" data-testid="cart-upsell-author">{authorNames(product) || brandName(product)}</span>}
              <OfferPrice product={product} />
            </div>
            <Button variant="secondary_s" onClick={() => add(product)} className="shrink-0 min-w-0 gap-1 px-3" data-testid="cart-upsell-add">
              <PlusIcon className="w-4 h-4 stroke-2" />
              {t`Add`}
            </Button>
          </motion.div>
        ))}
        </AnimatePresence>
      </div>
      {choosing && (
        <UpsellProductPanel
          product={choosing}
          onClose={() => setChoosing(null)}
          onNavigate={() => {
            setChoosing(null)
            store.hideCart()
          }}
        />
      )}
      </section>
    </>
  )
}
